import { parseApiDate } from './format';

export const NUMBER_LOCALE = 'en-US';



const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));



/** Shown wherever a value is genuinely absent (never render "0" for missing). */

export const EMPTY_VALUE = '—';

export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}



/**

 * Base formatter. `min`/`max` are fraction digits.

 * Returns EMPTY_VALUE for null/undefined/NaN so callers don't each re-check.

 */

export function formatNumber(value, { min = 0, max = 2 } = {}) {

  if (!isNum(value)) return EMPTY_VALUE;

  return Number(value).toLocaleString(NUMBER_LOCALE, {

    minimumFractionDigits: min,

    maximumFractionDigits: max,

  });

}



/** tCO₂e — 2 decimals is the reporting convention. */

export const formatTonne = (v, { withUnit = false } = {}) => {

  const n = formatNumber(v, { min: 2, max: 2 });

  return withUnit && n !== EMPTY_VALUE ? `${n} tCO₂e` : n;

};



/** Electricity, always whole kWh. */

export const formatKwh = (v, { withUnit = false } = {}) => {

  const n = formatNumber(v, { min: 0, max: 0 });

  return withUnit && n !== EMPTY_VALUE ? `${n} kWh` : n;

};



/** Fuel volume. §U6: monthly charts use litres, totals/reports use KL. */

export const formatLitre = (v, { withUnit = false } = {}) => {

  const n = formatNumber(v, { min: 0, max: 1 });

  return withUnit && n !== EMPTY_VALUE ? `${n} L` : n;

};



export const formatKl = (v, { withUnit = false } = {}) => {

  const n = formatNumber(v, { min: 3, max: 3 });

  return withUnit && n !== EMPTY_VALUE ? `${n} KL` : n;

};



/** Floor area (m²) — whole numbers. */

export const formatArea = (v, { withUnit = false } = {}) => {

  const n = formatNumber(v, { min: 0, max: 0 });

  return withUnit && n !== EMPTY_VALUE ? `${n} m²` : n;

};



export const formatPercent = (v, digits = 1) => {

  if (!isNum(v)) return EMPTY_VALUE;

  return `${formatNumber(v, { min: digits, max: digits })}%`;

};



export const formatJpy = (v) => {

  if (!isNum(v)) return EMPTY_VALUE;

  return `¥${formatNumber(v, { min: 0, max: 0 })}`;

};



/**

 * Rounded figure for KPI tiles (§U4: "6,553 tCO₂e" on the tile, full precision

 * on hover). Pair with formatTonne() inside the Tooltip.

 */

export const formatCompact = (v) => formatNumber(v, { min: 0, max: 0 });



/** Emission factors stay decimal strings; grouping must not coerce them to Number. */
export function formatEf(value) {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const text = String(value).trim();
  const match = text.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return text;
  const grouped = match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${match[1]}${grouped}${match[3] ? `.${match[3]}` : ''}`;
}



/** yyyy/MM/dd — matches the mockup's table date column. */

export function formatDate(value) {

  if (!value) return EMPTY_VALUE;

  const d = parseApiDate(value);

  if (Number.isNaN(d.getTime())) return EMPTY_VALUE;

  const pad = (n) => String(n).padStart(2, '0');

  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;

}



/** dd/MM/yyyy — used by the reporting-period range in the dashboard header. */

export function formatDayMonthYear(value) {

  if (!value) return EMPTY_VALUE;

  const d = parseApiDate(value);

  if (Number.isNaN(d.getTime())) return EMPTY_VALUE;

  const pad = (n) => String(n).padStart(2, '0');

  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

}



/** Signed YoY delta for KPI chips: "▼ 90.0%" / "▲ 26.9%". */

export function formatDelta(pct) {

  if (!isNum(pct)) return EMPTY_VALUE;

  const arrow = Number(pct) < 0 ? '▼' : '▲';

  return `${arrow} ${formatPercent(Math.abs(Number(pct)))}`;

}
